// Spelled pitch — the score model's pitch truth.
//
// The score stores HOW a note is written ({step, alter, octave}); the sound
// (MIDI) is derived. This is the inverse of the chart-melody model (MIDI +
// optional spelling) and is what makes double accidentals, correct
// enharmonics and transposition-by-interval expressible: a major 2nd above
// Eb is F, never E#, and no semitone count can tell you that.

import { accidentalForKey, type Accidental, type KeyContext } from '../notes';

export type Step = 'C' | 'D' | 'E' | 'F' | 'G' | 'A' | 'B';

export const STEPS: readonly Step[] = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];

/** Semitones of each natural step above C. */
const STEP_PC: Record<Step, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

export interface SpelledPitch {
  step: Step;
  /** Chromatic alteration: -2 (double-flat) .. +2 (double-sharp). */
  alter: number;
  /** Scientific octave: C4 = middle C = MIDI 60. Octaves change at C. */
  octave: number;
}

/** MIDI number of a spelled pitch (C4 = 60). Cb4 is 59; B#3 is 60. */
export function midiOf(p: SpelledPitch): number {
  return 12 * (p.octave + 1) + STEP_PC[p.step] + p.alter;
}

/** Pitch class 0..11 of a spelled pitch. */
export function pitchClassOfSpelled(p: SpelledPitch): number {
  return ((STEP_PC[p.step] + p.alter) % 12 + 12) % 12;
}

const ACCIDENTAL_CHARS: Record<string, number> = {
  '#': 1,
  '♯': 1,
  x: 2,
  '𝄪': 2,
  b: -1,
  '♭': -1,
  '𝄫': -2,
};

/**
 * Parse "Eb4", "F##3", "Bbb5", "Gx2". Octave is required — a pitch without a
 * register is a pitch class, which is `notes.ts`'s job, not this module's.
 */
export function parseSpelledPitch(text: string): SpelledPitch {
  const match = text.trim().match(/^([A-Ga-g])([#♯x𝄪b♭𝄫]*)(-?\d+)$/u);
  if (!match) throw new Error(`Unrecognised pitch: "${text}"`);
  const step = match[1].toUpperCase() as Step;
  let alter = 0;
  for (const ch of match[2]) alter += ACCIDENTAL_CHARS[ch] ?? 0;
  if (alter < -2 || alter > 2) throw new Error(`Alteration out of range in "${text}"`);
  return { step, alter, octave: Number(match[3]) };
}

/** ASCII accidental suffix for an alteration: "", "#", "##", "b", "bb". */
export function accidentalText(alter: number): string {
  return alter >= 0 ? '#'.repeat(alter) : 'b'.repeat(-alter);
}

/** Format as "Eb4" / "F##3" (ASCII accidentals, matching chart spellings). */
export function formatSpelledPitch(p: SpelledPitch): string {
  return `${p.step}${accidentalText(p.alter)}${p.octave}`;
}

/** The note-name part only, e.g. "Eb" — the shape chart `spelledAs` uses. */
export function noteNameOf(p: SpelledPitch): string {
  return `${p.step}${accidentalText(p.alter)}`;
}

/**
 * Spell a MIDI note. An explicit `name` ("Eb", "F##") pins step+alter and the
 * octave is derived from it (so Cb5 correctly wraps to MIDI 71 = B4's sound);
 * otherwise the key's accidental preference picks between the single-sharp
 * and single-flat spelling of the black keys.
 */
export function spellMidi(
  midi: number,
  options: { name?: string; key?: KeyContext | null; prefer?: Accidental } = {},
): SpelledPitch {
  if (options.name) {
    const parsed = parseSpelledPitchName(options.name);
    const base = STEP_PC[parsed.step] + parsed.alter;
    const octave = Math.round((midi - base) / 12) - 1;
    const candidate = { ...parsed, octave };
    if (midiOf(candidate) !== midi) {
      throw new Error(`Spelling "${options.name}" cannot sound MIDI ${midi}`);
    }
    return candidate;
  }
  const prefer = options.prefer ?? accidentalForKey(options.key ?? null);
  const pc = ((midi % 12) + 12) % 12;
  const natural = (Object.entries(STEP_PC) as [Step, number][]).find(([, v]) => v === pc);
  if (natural) {
    return { step: natural[0], alter: 0, octave: Math.floor(midi / 12) - 1 };
  }
  // Black key: sharp of the step below or flat of the step above.
  const [step, alter] =
    prefer === 'flat'
      ? ([stepAtPc((pc + 1) % 12), -1] as const)
      : ([stepAtPc((pc - 1 + 12) % 12), 1] as const);
  const octave = Math.round((midi - (STEP_PC[step] + alter)) / 12) - 1;
  return { step, alter, octave };
}

/** Parse a note name without octave ("Eb", "F##") into step+alter. */
export function parseSpelledPitchName(name: string): { step: Step; alter: number } {
  const p = parseSpelledPitch(`${name}0`);
  return { step: p.step, alter: p.alter };
}

function stepAtPc(pc: number): Step {
  const entry = (Object.entries(STEP_PC) as [Step, number][]).find(([, v]) => v === pc);
  if (!entry) throw new Error(`No natural step at pitch class ${pc}`);
  return entry[0];
}

/**
 * A musical interval as (diatonic steps, semitones) — MusicXML's transpose
 * model. degrees=1/semitones=2 is a major 2nd; the pair is what distinguishes
 * an augmented 4th (3, 6) from a diminished 5th (4, 6).
 */
export interface DiatonicInterval {
  /** Diatonic steps (0 = unison, 1 = a 2nd, 4 = a 5th…). Negative = down. */
  degrees: number;
  /** Chromatic semitones. Negative = down. */
  semitones: number;
}

/** Common intervals by conventional name. */
export const INTERVALS: Record<string, DiatonicInterval> = {
  P1: { degrees: 0, semitones: 0 },
  A1: { degrees: 0, semitones: 1 },
  m2: { degrees: 1, semitones: 1 },
  M2: { degrees: 1, semitones: 2 },
  A2: { degrees: 1, semitones: 3 },
  m3: { degrees: 2, semitones: 3 },
  M3: { degrees: 2, semitones: 4 },
  P4: { degrees: 3, semitones: 5 },
  A4: { degrees: 3, semitones: 6 },
  d5: { degrees: 4, semitones: 6 },
  P5: { degrees: 4, semitones: 7 },
  m6: { degrees: 5, semitones: 8 },
  M6: { degrees: 5, semitones: 9 },
  m7: { degrees: 6, semitones: 10 },
  M7: { degrees: 6, semitones: 11 },
  P8: { degrees: 7, semitones: 12 },
};

/** Invert an interval's direction (M2 up ↔ M2 down). */
export function invertInterval(i: DiatonicInterval): DiatonicInterval {
  return { degrees: -i.degrees, semitones: -i.semitones };
}

/**
 * Transpose by a diatonic interval: the letter moves by `degrees`, then the
 * alteration is whatever makes the sound move by exactly `semitones`. This is
 * the operation MIDI-first models cannot express (Eb + M2 = F, not E#).
 * Throws if the result needs an alteration beyond double sharp/flat — callers
 * respell the input rather than store an unprintable pitch.
 */
export function transposePitch(p: SpelledPitch, interval: DiatonicInterval): SpelledPitch {
  const fromIndex = STEPS.indexOf(p.step) + 7 * p.octave;
  const toIndex = fromIndex + interval.degrees;
  const step = STEPS[((toIndex % 7) + 7) % 7];
  const octave = Math.floor(toIndex / 7);
  const targetMidi = midiOf(p) + interval.semitones;
  const alter = targetMidi - (12 * (octave + 1) + STEP_PC[step]);
  if (alter < -2 || alter > 2) {
    throw new Error(
      `Transposing ${formatSpelledPitch(p)} by (${interval.degrees}, ${interval.semitones}) needs alter ${alter}`,
    );
  }
  return { step, alter, octave };
}
